// Événements - Version avec création fonctionnelle
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, MapPin, Users, Clock, Plus, Search, Heart, Share2, Star, CalendarPlus, X, Loader2, Image, Euro, ChevronRight, ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DEMO_EVENEMENTS = [];

const CATEGORIES = ['Tous', 'Salon professionnel', 'Atelier', 'Conférence', 'Networking', 'Formation'];
const EVENT_CATEGORIES = ['Salon professionnel', 'Atelier', 'Conférence', 'Networking', 'Formation'];

// Modal de création d'événement
function CreateEventModal({ isOpen, onClose, onCreate }) {
  const [titre, setTitre] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [heureDebut, setHeureDebut] = useState('14:00');
  const [heureFin, setHeureFin] = useState('17:00');
  const [lieu, setLieu] = useState('');
  const [categorie, setCategorie] = useState('Atelier');
  const [prix, setPrix] = useState('');
  const [placesMax, setPlacesMax] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [creating, setCreating] = useState(false);
  const [isMobile] = useState(window.innerWidth < 768);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (!isOpen || !isMobile) { setKeyboardHeight(0); return; }
    const viewport = window.visualViewport;
    if (!viewport) return;
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    return () => {
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [isOpen, isMobile]);

  const handleSubmit = async () => {
    if (!titre.trim()) { toast.error('Donne un titre à ton événement'); return; }
    if (!description.trim()) { toast.error('Ajoute une description'); return; }
    if (!date) { toast.error('Choisis une date'); return; }
    if (!lieu.trim()) { toast.error('Indique le lieu'); return; }
    setCreating(true);
    const dateObj = new Date(date);
    const formattedDate = dateObj.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    await onCreate({
      titre, description,
      date: formattedDate,
      heure: `${heureDebut} - ${heureFin}`,
      lieu, categorie,
      prix: prix ? `${prix}€` : 'Gratuit',
      places_max: placesMax ? parseInt(placesMax) : null,
      image: imageUrl || null
    });
    setCreating(false);
    setTitre(''); setDescription(''); setDate(''); setLieu('');
    setPrix(''); setPlacesMax(''); setImageUrl('');
    onClose();
  };

  if (!isOpen) return null;

  const formFields = (
    <>
      <div>
        <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Titre *</label>
        <input type="text" value={titre} onChange={e => setTitre(e.target.value)}
          placeholder="Ex: Atelier bricolage débutants"
          className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
          data-testid="event-title" />
      </div>
      <div>
        <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Description *</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
          placeholder="Décris ton événement, ce que les participants vont découvrir..."
          className="w-full p-4 rounded-xl border text-[15px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 resize-none"
          data-testid="event-description" />
      </div>
      <div>
        <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Catégorie</label>
        <div className="relative flex items-center">
          <div className="flex gap-1.5 overflow-x-auto flex-1 pr-6" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {EVENT_CATEGORIES.map(cat => (
              <button key={cat} type="button" onClick={() => setCategorie(cat)}
                className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all ${
                  categorie === cat ? 'bg-orange-500 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200'
                }`}>
                {cat}
              </button>
            ))}
          </div>
          <ChevronRight size={18} className="text-orange-500 pointer-events-none flex-shrink-0" strokeWidth={2.5} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Date *</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200"
            data-testid="event-date" />
        </div>
        <div>
          <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Début</label>
          <input type="time" value={heureDebut} onChange={e => setHeureDebut(e.target.value)}
            className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200" />
        </div>
        <div>
          <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Fin</label>
          <input type="time" value={heureFin} onChange={e => setHeureFin(e.target.value)}
            className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200" />
        </div>
      </div>
      <div>
        <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Lieu *</label>
        <div className="relative">
          <MapPin size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={lieu} onChange={e => setLieu(e.target.value)}
            placeholder="Adresse ou 'En ligne'"
            className="w-full pl-11 p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
            data-testid="event-location" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Prix (€)</label>
          <div className="relative">
            <Euro size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="number" value={prix} onChange={e => setPrix(e.target.value)}
              placeholder="Gratuit" min="0"
              className="w-full pl-11 p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200" />
          </div>
        </div>
        <div>
          <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Places max</label>
          <div className="relative">
            <Users size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="number" value={placesMax} onChange={e => setPlacesMax(e.target.value)}
              placeholder="Illimité" min="1"
              className="w-full pl-11 p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200" />
          </div>
        </div>
      </div>
      <div>
        <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Image (URL)</label>
        <input type="url" value={imageUrl} onChange={e => setImageUrl(e.target.value)}
          placeholder="https://..."
          className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400" />
      </div>
    </>
  );

  if (isMobile) {
    return createPortal(
      <motion.div
        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="fixed inset-0 z-[10002] flex flex-col bg-white"
      >
        <header className="flex items-center gap-2 px-3 border-b border-gray-100"
          style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}>
          <button onClick={onClose} className="p-1 text-gray-500"><ArrowLeft size={22} /></button>
          <h1 className="text-[15px] font-bold text-gray-900">Créer un événement</h1>
        </header>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">{formFields}</div>
        <div className="p-4 border-t border-gray-100 bg-white"
          style={{
            paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
            marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
          }}>
          <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200">
            <Sparkles size={16} />Créer avec Abracada
          </button>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 h-12 rounded-xl font-semibold border border-gray-300 text-[#1e3a5f]">Annuler</button>
            <button onClick={handleSubmit} disabled={creating}
              className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 flex items-center justify-center disabled:opacity-50">
              {creating ? <Loader2 size={18} className="animate-spin" /> : 'Créer'}
            </button>
          </div>
        </div>
      </motion.div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[10002] bg-black/60 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Créer un événement</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} className="text-gray-500" /></button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">{formFields}</div>
        <div className="p-6 border-t border-gray-200 flex gap-3">
          <button onClick={onClose} className="flex-1 h-12 rounded-xl font-semibold border border-gray-300 text-[#1e3a5f]">Annuler</button>
          <button onClick={handleSubmit} disabled={creating}
            className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 flex items-center justify-center disabled:opacity-50">
            {creating ? <Loader2 size={18} className="animate-spin" /> : "Créer l'événement"}
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}

export default function EvenementsPage() {
  const { user } = useCurrentUser();
  const [filter, setFilter] = useState('Tous');
  const [searchQuery, setSearchQuery] = useState('');
  const [showMesEvents, setShowMesEvents] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [evenements, setEvenements] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Refs et états pour le scroll des filtres mobile
  const scrollContainerRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);
  
  // Gérer le scroll des filtres
  const handleScrollFilters = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const isDemo = user?.isDemo;

  // Charger les événements depuis l'API
  const fetchEvents = async () => {
    if (isDemo) {
      setEvenements(DEMO_EVENEMENTS);
      setLoading(false);
      return;
    }
    
    const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/cockpit/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Combiner créés et rejoints
        const all = [...(data.created || []), ...(data.joined || [])];
        setEvenements(all);
      }
    } catch (err) {
      console.error('Erreur chargement événements:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user !== null) {
      fetchEvents();
    }
  }, [user, isDemo]);

  const filteredEvents = evenements.filter(event => {
    const matchesCategory = filter === 'Tous' || event.categorie === filter;
    const matchesSearch = event.titre.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          event.lieu.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesInscrit = !showMesEvents || event.inscrit;
    return matchesCategory && matchesSearch && matchesInscrit;
  });

  const mesInscriptions = evenements.filter(e => e.inscrit);

  const handleCreateEvent = () => {
    if (isDemo) {
      toast.error('Crée ton compte pour organiser un événement !');
      return;
    }
    setShowCreateModal(true);
  };

  const handleCreate = async (eventData) => {
    const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('token');
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/cockpit/events`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(eventData)
      });
      
      if (res.ok) {
        const data = await res.json();
        setEvenements(prev => [{ ...data.event, inscrit: true, is_organizer: true }, ...prev]);
        toast.success('Événement créé !');
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (err) {
      console.error('Erreur création événement:', err);
      toast.error('Erreur réseau');
    }
  };

  const handleDelete = async (eventId) => {
    if (isDemo) return;
    if (!confirm('Supprimer cet événement ?')) return;
    
    const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('token');
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/cockpit/events/${eventId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setEvenements(prev => prev.filter(e => e.id !== eventId));
        toast.success('Événement supprimé');
      }
    } catch (err) {
      console.error('Erreur suppression:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateEventModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onCreate={handleCreate}
          />
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Événements</h1>
          <p className="text-sm text-gray-500 mt-1">
            Découvre les événements de la communauté
          </p>
        </div>
        <button 
          onClick={handleCreateEvent}
          className="flex items-center gap-2 px-4 py-2 bg-[#1E3A5F] hover:bg-[#1E3A5F]/90 text-white font-medium rounded-lg transition-colors"
          data-testid="create-event-btn"
        >
          <Plus size={18} />
          Créer un événement
        </button>
      </div>

      {/* Banner démo */}
      {isDemo && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <Calendar size={20} className="text-amber-500" />
          <div>
            <p className="font-medium text-amber-800">Mode Démonstration</p>
            <p className="text-sm text-amber-700">Ces événements sont des exemples. Crée ton compte pour t'inscrire ou organiser des événements.</p>
          </div>
        </div>
      )}

      {/* Mes inscriptions */}
      {mesInscriptions.length > 0 && (
        <div className="bg-gradient-to-r from-[#FFF8F4] to-[#FEF3E8] border border-[#1E3A5F]/20 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-[#1E3A5F]">Mes événements ({mesInscriptions.length})</h3>
            <button 
              onClick={() => setShowMesEvents(!showMesEvents)}
              className="text-sm text-[#1E3A5F] hover:underline"
            >
              {showMesEvents ? 'Voir tous' : 'Voir uniquement'}
            </button>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {mesInscriptions.map(event => (
              <div key={event.id} className="flex-shrink-0 flex items-center gap-3 bg-white rounded-lg p-3 border border-[#1E3A5F]/10">
                <img src={event.image} alt={event.titre} className="w-12 h-12 rounded-lg object-cover" />
                <div>
                  <p className="font-medium text-gray-900 text-sm">{event.titre}</p>
                  <p className="text-xs text-gray-500">{event.date}</p>
                </div>
                {event.is_organizer && (
                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">Organisateur</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filtres - Style premium */}
      <div className="bg-white rounded-xl border border-gray-200 p-3">
        {/* Recherche */}
        <div className="flex items-center gap-2 mb-3 px-2.5 py-1.5 border border-gray-200 rounded-lg">
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Rechercher un événement..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent border-0 outline-none text-xs text-gray-700 placeholder-gray-400"
          />
        </div>

        {/* Filtres Mobile */}
        <div className="sm:hidden">
          <div className="relative flex items-center">
            <div 
              ref={scrollContainerRef}
              onScroll={handleScrollFilters}
              className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  data-testid={`filter-${cat}`}
                  className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                    filter === cat
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            
            {/* Flèche orange discrète */}
            {showScrollHint && (
              <div className="flex-shrink-0 flex items-center pointer-events-none">
                <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
              </div>
            )}
          </div>
        </div>

        {/* Filtres Desktop */}
        <div className="hidden sm:flex flex-wrap gap-2">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                filter === cat
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* État vide */}
      {!isDemo && evenements.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <CalendarPlus size={32} className="text-blue-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun événement</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Tu n'as pas encore créé ou rejoint d'événement. Crée le tien ou explore les événements de la communauté !
          </p>
          <button 
            onClick={handleCreateEvent}
            className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
          >
            Créer un événement
          </button>
        </div>
      )}

      {/* Liste des événements */}
      {filteredEvents.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredEvents.map(event => (
            <div key={event.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow group">
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={event.image} 
                  alt={event.titre}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-3 left-3">
                  <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-gray-900 text-xs font-medium rounded-full">
                    {event.categorie}
                  </span>
                </div>
                <div className="absolute top-3 right-3">
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                    event.prix === 'Gratuit' 
                      ? 'bg-[#1E3A5F] text-white' 
                      : 'bg-[#F97316] text-white'
                  }`}>
                    {event.prix}
                  </span>
                </div>
                {event.inscrit && (
                  <div className="absolute bottom-3 right-3">
                    <span className="px-3 py-1 bg-[#1E3A5F] text-white text-xs font-medium rounded-full flex items-center gap-1">
                      <Star size={12} fill="white" />
                      {event.is_organizer ? 'Organisateur' : 'Inscrit'}
                    </span>
                  </div>
                )}
              </div>

              {/* Contenu */}
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-[#1E3A5F] transition-colors">
                  {event.titre}
                </h3>
                <p className="text-sm text-gray-500 mb-4 line-clamp-2">{event.description}</p>

                {/* Infos */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar size={16} className="text-[#1E3A5F]" />
                    <span>{event.date}</span>
                    <span className="text-gray-300">•</span>
                    <Clock size={16} className="text-[#1E3A5F]" />
                    <span>{event.heure}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin size={16} className="text-[#1E3A5F]" />
                    <span>{event.lieu}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users size={16} className="text-[#1E3A5F]" />
                    <span>{event.participants} participants</span>
                    {event.interesse > 0 && (
                      <>
                        <span className="text-gray-300">•</span>
                        <span>{event.interesse} intéressés</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className="text-xs text-gray-500">Par {event.organisateur}</span>
                  <div className="flex items-center gap-2">
                    <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                      <Heart size={18} className="text-gray-400" />
                    </button>
                    <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                      <Share2 size={18} className="text-gray-400" />
                    </button>
                    {event.is_organizer ? (
                      <button 
                        onClick={() => handleDelete(event.id)}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                      >
                        Supprimer
                      </button>
                    ) : (
                      <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        event.inscrit 
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                          : 'bg-[#1E3A5F] text-white hover:bg-[#1E3A5F]/90'
                      }`}>
                        {event.inscrit ? 'Voir détails' : 'S\'inscrire'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {evenements.length > 0 && filteredEvents.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
          <p>Aucun événement trouvé</p>
        </div>
      )}
    </div>
  );
}
